import { Component, EventEmitter, Input, OnInit, Output, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { DeltaPostingService } from './delta-posting.service';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
import * as _ from 'lodash-es';

@Component({
    selector: 'gtw-delta-posting',
    templateUrl: './delta-posting.component.html',
    styleUrls: ['./delta-posting.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class DeltaPostingComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // 🚀 CRITICAL: Filter params input for dynamic data
  filterParams: any = {};
   
  @Input('filter-params')
  set _filter_params(filterParams: string){
    const params = safeJsonInput<any>(filterParams, null, 'delta-posting.filter-params');
    if (params && Object.keys(params).length > 0) {
      this.filterParams = params;
      this.init();
    }
  }

  // 🔴 GLOBAL SETTINGS AS @INPUT - DO NOT CREATE NEW SERVICES
  @Input('base-urls')
  set _base_urls(baseUrls: string){
    const urls = safeJsonInput<GtwElementsBaseUrlsModel | null>(baseUrls, null, 'delta-posting.base-urls');
    if (urls && Object.keys(urls).length > 0) {
      this.baseURLs = urls;
      this.init();
    }
  }
  baseURLs: GtwElementsBaseUrlsModel = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};

  @Input('row-data')
  set _row_data(rowData: string){
    const data = safeJsonInput<any>(rowData, null, 'delta-posting.row-data');
    if (data && Object.keys(data).length > 0) {
      this.rowData = data;
      this.init();
    }
  }
  rowData: any = {};

  @Input('header-data')
  set _header_data(headerData: string){
    const header = safeJsonInput<any>(headerData, null, 'delta-posting.header-data');
    if (header && Object.keys(header).length > 0) {
      this.globalService = header.globalParams;
      this.USER_SETTINGS = header.userSettingsData;
      this.SERVER_CONFIG = header.serverConfigData;
      this.GENERAL_CONFIG = header.generalConfigData;
      this.init();
    }
  }

  // Modal configuration - following AngularJS pattern
  modalConfig: ConfirmationModalConfig = {
    title: 'Confirm',
    message: 'Are you sure you want to trigger posting for the selected partnership?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Processing...'
  };

  // Component properties following fir163jsync pattern
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};
  
  showModal: boolean = false;
  syncSaveDtls: any[] = [];
  logged_in_user: string = '';
  modalName: string = '';
  isSaveClicked: boolean = false;

  constructor(
    private gtwSnackbarService: GtwSnackbarService,
    private deltaPostingService: DeltaPostingService
  ) { }

  ngOnInit(): void {
    this.init();
  }

  init(): void {
    // Following fir163jsync pattern - check if all required data is available
    if (this.baseURLs && Object.keys(this.baseURLs).length > 0 && 
        this.filterParams && Object.keys(this.filterParams).length > 0 &&
        this.GENERAL_CONFIG && Object.keys(this.GENERAL_CONFIG).length > 0) {
      this.initializeData();
    }
  }

  initializeData(): void {
    // Get logged in user from USER_SETTINGS like fir163jsync
    if (this.USER_SETTINGS?.user?.sso_id) {
      this.logged_in_user = this.USER_SETTINGS.user.sso_id;
    }
    
    // Get modal name from globalService
    if (this.globalService?.modalScreen?.modal_name) {
      this.modalName = this.globalService.modalScreen.modal_name;
    }
    
    // Process grid data if rowData is available
    if (this.rowData) {
      this.processGridData();
    }
  }

  processGridData(): void {
    // Process grid data exactly like AngularJS controller
    if (this.rowData != null) {
      this.syncSaveDtls = [];
      // Push rowData to syncSaveDtls like AngularJS: vm.syncSaveDtls.push(rowData);
      this.syncSaveDtls.push(this.rowData);
    }
  }

  onConfirm(): void {
    this.confirmPosting();
  }

  cancel(): void {
    // Following fir163jsync pattern - dispatch CustomEvent to close modal
    this.showModal = false;
    
    const updateEvent = new CustomEvent('request-grid-update', {
      detail: 'cancel',
      bubbles: true
    });
    
    if (document.querySelector('gtw-delta-posting-ce')) {
      document.querySelector('gtw-delta-posting-ce')?.dispatchEvent(updateEvent);
    }
  }
  
  onCancel(): void {
    // Called from confirmation modal - delegates to cancel()
    this.cancel();
  }

  confirmPosting(): void {
    // Follow exact AngularJS confirm_posting function behavior
    const returnClobSettingsObj: any = {};
    
    // Build settings object with real data from rowData and filterParams (NOT hardcoded)
    returnClobSettingsObj['tax_year'] = this.filterParams?.tax_year || this.globalService?.globalParams?.tax_year || '';
    returnClobSettingsObj['scenario'] = this.filterParams?.scenario || this.globalService?.globalParams?.scenario || '';
    returnClobSettingsObj['jcd_key'] = this.filterParams?.jcd_key || this.globalService?.globalParams?.jcd_key || '';
    returnClobSettingsObj['sso_id'] = this.logged_in_user;
    returnClobSettingsObj['combination_key'] = this.rowData?.COMBINATION_KEY || '';
    returnClobSettingsObj['tp_key'] = this.rowData?.TP_KEY || '';
    returnClobSettingsObj['pship_ptr_posting_key'] = this.rowData?.PSHIP_PTR_POSTING_KEY || '';

    const ecSaveDtls = this.syncSaveDtls;
    const message = "Delta Posting successfully completed.";
    
    // Call sendDetails function like AngularJS
    this.sendDetails([returnClobSettingsObj], ecSaveDtls, message);
  }

  sendDetails(returnClobSettingsObj: any[], ecSaveDtls: any[], message: string): void {
    // Follow exact AngularJS sendDetails function behavior
    // Show loader (equivalent to $rootScope.$broadcast("showLoader", true))
    this.modalConfig = { ...this.modalConfig, loading: true };
    // Call service method like AngularJS
    this.deltaPostingService.triggerDeltaPosting(
      this.GENERAL_CONFIG.base_url, // Use base_url like fir163jsync
      returnClobSettingsObj, // Pass returnClobSettingsObj array
      ecSaveDtls, // Pass ecSaveDtls array (syncSaveDtls)
      this.globalService, // Pass globalService like fir163jsync
      this.filterParams, // Pass filterParams
      this.rowData // Pass rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        this.modalConfig = { ...this.modalConfig, loading: false };

        if (result.errorMessage && result.errorMessage !== 'null') {
          this.isSaveClicked = false;
          this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
        } else {
          // Show success message (equivalent to AlertService.add("success", message, 4000))
          this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--green');
          
          // CRITICAL: Emit 'refresh' string for AngularJS modal close
          this.requestGridUpdate.emit('refresh');
        }
      },
      (error: any) => {
        this.modalConfig = { ...this.modalConfig, loading: false };
        this.cancel();
        this.isSaveClicked = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while processing. Please try again.', 'gtw-snackbar--red');
        console.error('Delta posting error:', error);
      }
    );
  }
}

